<template>
  <div v-if="value" class="rp" role="dialog" aria-modal="true" aria-label="Verification report preview">
    <div class="rp__scrim" @click="close"></div>

    <div class="rp__sheet">
      <header class="rp__bar">
        <span class="rp__bar-title">Report preview</span>
        <span class="rp__bar-count">{{ pageCountLabel }}</span>
        <div class="rp__bar-actions">
          <button
            type="button"
            class="rp__btn rp__btn--primary"
            @click="onDownloadPdf"
          >{{ drawingPending ? 'Preparing…' : 'Download PDF' }}</button>
          <button type="button" class="rp__btn" @click="close">Close</button>
        </div>
      </header>

      <!-- EDITING LIVES OUTSIDE THE SHEETS, and has to. The sheets below are a
           reproduction of the printed page down to the millimetre, so nothing
           inside them can be a form control without breaking the very fidelity
           they exist to provide. Everything typed here appears on the sheets
           immediately, which is the same feedback loop the inline notes editor
           gave, just relocated. -->
      <section class="rp__edit" aria-label="Report fields">
        <div class="rp__fields">
          <label v-for="field in editableFields" :key="field.key" class="rp__field">
            <span class="rp__field-label">{{ field.label }}</span>
            <input
              type="text"
              class="rp__field-input"
              :value="field.value"
              :placeholder="field.placeholder"
              :maxlength="fieldMaxLength"
              @input="onOverride(field.key, $event.target.value)"
            />
          </label>
        </div>
        <NotesEditor />
      </section>

      <div class="rp__pages">
        <!-- One element per draw command, positioned in millimetres scaled to
             pixels. The commands come from the SAME layoutReport() call the PDF
             consumes, so page count, line breaks and page breaks are identical
             by construction rather than by agreement. -->
        <section
          v-for="page in pages"
          :key="page.index"
          class="rp__page"
          :style="pageStyle"
          :aria-label="`Page ${page.index + 1} of ${pageCount}`"
        >
          <template v-for="(cmd, i) in page.commands">
            <img
              v-if="cmd.kind === 'image'"
              :key="`i-${i}`"
              class="rp__img"
              :src="cmd.dataUrl"
              :alt="cmd.alt || ''"
              :style="imageStyle(cmd)"
            />
            <div
              v-else-if="cmd.kind === 'rule'"
              :key="`r-${i}`"
              class="rp__rule"
              :style="ruleStyle(cmd)"
            ></div>
            <span
              v-else
              :key="`t-${i}`"
              class="rp__text"
              :style="textStyle(cmd)"
            >{{ cmd.text }}</span>
          </template>
        </section>

        <p v-if="!pageCount" class="rp__empty">Nothing to preview yet.</p>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * AVA-475 — on-screen report preview, page-accurate.
 *
 * PAGE COUNT PARITY IS THE FEATURE. This used to be one long scrolling column
 * of semantic HTML while the PDF flowed onto A4 pages, so the preview could not
 * answer the only structural question anyone asks of it — how many pages is
 * this, and what lands where. It now renders `layoutReport()`'s draw commands
 * onto discrete A4 sheets, which is the identical command stream `reportPdf.js`
 * draws. Neither renderer measures or breaks anything itself.
 *
 * THE TRADE, STATED PLAINLY: the semantic table/definition-list markup is gone,
 * replaced by absolutely-positioned text. That markup could not survive
 * millimetre fidelity — a `<table>` lays itself out, which is precisely the
 * second, competing layout engine this change removes. The text is still real
 * text in reading order, each sheet is labelled with its page number, and
 * evidence images carry alt text supplied by the layout.
 *
 * Glyph widths differ by a hair between the browser's Helvetica/Arial and
 * jsPDF's built-in AFM metrics. That shifts nothing structurally — every line
 * break and page break was already decided upstream — it only means a
 * right-aligned column can sit a fraction of a millimetre off its printed twin.
 *
 * AUTHORITY: our computed values win. The reference docx's own sample verdicts
 * are not reproduced and must not be copied — its P02 Eastings row marks `+5`
 * against `±3mm` as PASS, which is simply wrong. Every verdict comes from
 * `evaluatePoint` via GET_REPORT_MODEL.
 *
 * Controlled by `value` (v-model) so the owning view holds the open/closed flag;
 * this component keeps no visibility state of its own.
 */
import { mapGetters } from 'vuex';
import NotesEditor from '@/features/Verification/components/report/NotesEditor.vue';
import { error as debugError, log as debugLog } from '@/utils/debugLogger';
import { generateReportPdf } from '@/features/Verification/utils/reportPdf';
import {
  COLOR,
  PAGE,
  PT_TO_MM,
  createMeasurer,
  layoutReport,
} from '@/features/Verification/utils/reportLayout';
import {
  REPORT_OVERRIDE_FIELDS,
  REPORT_OVERRIDE_MAX_LENGTH,
} from '@/features/Verification/modules/verification.module';

/**
 * On-screen millimetre. 3.6 puts an A4 sheet at 756 px — inside the 920 px
 * dialog with room for its padding — and leaves 9 pt body text at ~11 px, which
 * is readable without a zoom control.
 */
const PX_PER_MM = 3.6;

/** Template labels, EXACTLY as the AVA-356 metadata table names them. */
const FIELD_LABELS = Object.freeze({
  handbookNo: 'Handbook No.',
  modelVersion: 'Model Version',
  toleranceSpec: 'Tolerance Spec',
  revision: 'Revision',
});

const mmToPx = (mm) => `${mm * PX_PER_MM}px`;

export default {
  name: 'ReportPreview',
  components: { NotesEditor },
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      // Mirrors the store's clamp, so the input stops the surveyor at the same
      // point the mutation would have truncated them silently.
      fieldMaxLength: REPORT_OVERRIDE_MAX_LENGTH,
      // True while the reference drawing is being fetched. Reactive because the
      // Download button says so — the click now waits on that fetch, and a
      // button that appears to do nothing for a second is worse than a label.
      drawingPending: false,
    };
  },
  created() {
    // The in-flight resolve, awaited by onDownloadPdf. Non-reactive: it is a
    // promise handle, not state anything renders.
    this.drawingReady = null;
    // Non-reactive on purpose: it is a measurement tool, not state, and Vue
    // walking a jsPDF instance's object graph would be pure waste. Built lazily
    // (see `measurer()`) so a closed dialog costs nothing.
    this.pdfMeasurer = null;
  },
  watch: {
    /**
     * Escape-to-close is bound on `document`, not on the dialog element. A
     * `@keydown.esc` on the overlay div only fires while that div holds focus,
     * which it never does — the div is not focusable and focus stays wherever
     * the surveyor left it. The listener is added on open and removed on close
     * so it can never outlive the dialog.
     */
    value: {
      immediate: true,
      handler(open) {
        if (open) {
          document.addEventListener('keydown', this.onKeydown);
          // Resolved on OPEN, not on selection: it fetches and decodes a file,
          // and most sessions never open the report. Re-resolving each time
          // also guarantees a re-selected drawing is the one that prints.
          //
          // THE PROMISE IS KEPT, and `onDownloadPdf` waits on it. This is a
          // network fetch and the Download button is right there in the
          // toolbar: a surveyor who clicks it within the second used to get a
          // PDF with no drawing page while the preview grew one moments later.
          this.drawingPending = true;
          this.drawingReady = this.$store
            .dispatch('VERIFICATION/RESOLVE_REPORT_DRAWING')
            .catch((err) => debugError('[ReportPreview] reference drawing failed to resolve', err))
            .finally(() => { this.drawingPending = false; });
        } else {
          document.removeEventListener('keydown', this.onKeydown);
        }
      },
    },
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  computed: {
    ...mapGetters('VERIFICATION', {
      reportModel: 'GET_REPORT_MODEL',
      overrides: 'GET_REPORT_OVERRIDES',
    }),

    /**
     * Skipped entirely while closed. This component is always instantiated by
     * its parent (only its DOM is behind `v-if`), so without the guard every
     * unrelated store change would re-paginate a report nobody is looking at.
     */
    layout() {
      if (!this.value) return { pages: [], pageCount: 0 };
      return layoutReport(this.reportModel, { measure: this.measurer() });
    },

    pages() {
      return this.layout.pages;
    },

    pageCount() {
      return this.layout.pageCount;
    },

    pageCountLabel() {
      return `${this.pageCount} ${this.pageCount === 1 ? 'page' : 'pages'}`;
    },

    pageStyle() {
      return {
        width: mmToPx(PAGE.width),
        height: mmToPx(PAGE.height),
      };
    },

    /**
     * The four header facts no API supplies. The placeholder shows the system
     * value when one exists, so it is obvious that typing here REPLACES
     * something rather than filling a void.
     */
    editableFields() {
      const support = this.reportModel.support || {};
      return REPORT_OVERRIDE_FIELDS.map((key) => ({
        key,
        label: FIELD_LABELS[key] || key,
        value: this.overrides[key] || '',
        placeholder: support[key] ? `System: ${support[key]}` : 'Not supplied',
      }));
    },
  },
  methods: {
    measurer() {
      if (!this.pdfMeasurer) this.pdfMeasurer = createMeasurer();
      return this.pdfMeasurer;
    },

    textStyle(cmd) {
      const style = {
        top: mmToPx(cmd.top),
        fontSize: mmToPx(cmd.size * PT_TO_MM),
        fontWeight: cmd.style === 'bold' ? '700' : '400',
        color: `rgb(${cmd.color.join(',')})`,
      };
      // A right-aligned command names its cell's RIGHT edge, so it is anchored
      // from the right of the sheet — the same convention jsPDF's
      // `{ align: 'right' }` uses.
      if (cmd.align === 'right') style.right = mmToPx(PAGE.width - cmd.x);
      else style.left = mmToPx(cmd.x);
      return style;
    },

    ruleStyle(cmd) {
      return {
        top: mmToPx(cmd.y),
        left: mmToPx(cmd.x),
        width: mmToPx(cmd.width),
        // Falls back to a real colour, never to `rgb()` — an invalid value here
        // fails silently and the rule just disappears.
        background: `rgb(${(cmd.color || COLOR.rule).join(',')})`,
      };
    },

    imageStyle(cmd) {
      return {
        top: mmToPx(cmd.y),
        left: mmToPx(cmd.x),
        width: mmToPx(cmd.width),
        height: mmToPx(cmd.height),
      };
    },

    onOverride(key, value) {
      this.$store.dispatch('VERIFICATION/setReportOverride', { key, value });
    },

    /**
     * The PDF is built from the SAME `GET_REPORT_MODEL` this preview renders,
     * not from the DOM — so what downloads cannot drift from what was reviewed.
     *
     * Waits for the reference drawing first. Without that wait the model read
     * below is a race against a network fetch, and losing it silently drops the
     * drawing page from the download while leaving it in the preview — a
     * difference nobody would think to check for.
     */
    async onDownloadPdf() {
      try {
        if (this.drawingReady) await this.drawingReady;
        debugLog('[ReportPreview] exporting PDF', {
          pages: this.pageCount,
          hasDrawing: !!this.reportModel.drawing,
        });
        generateReportPdf(this.reportModel);
      } catch (err) {
        debugError('[ReportPreview] PDF export failed', err);
      }
    },

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
    },

    close() {
      this.$emit('input', false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.rp {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
}

.rp__scrim {
  position: absolute;
  inset: 0;
  background: rgba(2, 26, 26, 0.55);
}

.rp__sheet {
  position: relative;
  width: min(920px, 92vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: $vf-panel;
  border-radius: $vf-r-lg;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.32);
  overflow: hidden;
}

.rp__bar {
  flex: none;
  display: flex;
  align-items: center;
  gap: $vf-md;
  padding: $vf-sm $vf-md;
  background: $vf-dialog-head;
  color: #fff;
}

.rp__bar-title {
  font-family: $vf-font-heading;
  font-size: 13px;
  font-weight: 600;
}

.rp__bar-count {
  font-family: $vf-font-mono;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.72);
}

.rp__bar-actions {
  display: flex;
  gap: $vf-sm;
  margin-left: auto;
}

.rp__btn {
  font-family: $vf-font-body;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: $vf-r-sm;
  padding: 5px 12px;
  cursor: pointer;

  &:hover { background: rgba(255, 255, 255, 0.2); }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 1px; }

  &--primary {
    background: $vf-brand-emerald;
    border-color: $vf-brand-emerald;
    color: $vf-brand-rich;

    &:hover { background: lighten($vf-brand-emerald, 6%); }
  }
}

// ---- Editing surface ------------------------------------------------------
.rp__edit {
  flex: none;
  display: grid;
  gap: $vf-md;
  padding: $vf-md;
  border-bottom: 1px solid $vf-line;
  background: $vf-panel;
  max-height: 34vh;
  overflow-y: auto;
}

.rp__fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: $vf-sm $vf-md;
}

.rp__field {
  display: grid;
  gap: 3px;
}

.rp__field-label {
  font-family: $vf-font-heading;
  font-size: 11px;
  font-weight: 600;
  color: $vf-ink-2;
}

.rp__field-input {
  font-family: $vf-font-body;
  font-size: 12px;
  color: $vf-ink;
  background: $vf-panel-2;
  border: 1px solid $vf-line;
  border-radius: $vf-r-sm;
  padding: 5px $vf-sm;

  &::placeholder {
    color: $vf-ink-3;
    font-style: italic;
  }

  &:focus-visible {
    outline: 2px solid $vf-primary;
    outline-offset: 1px;
  }
}

// ---- Sheets ---------------------------------------------------------------
.rp__pages {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: $vf-lg;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $vf-lg;
  // The scrim behind paper. Sheets are white and cast a shadow, so they need a
  // darker ground than the panel to read as sheets rather than as a page.
  background: #e9edec;
}

.rp__page {
  position: relative;
  flex: none;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.18);
  overflow: hidden;
}

// Every child is placed by the layout, in millimetres. Nothing in here may
// participate in normal flow — a wrapped line would be a line the PDF does not
// have.
.rp__text {
  position: absolute;
  // Helvetica first: it is what jsPDF measures with. Arial is the metric-
  // compatible stand-in on platforms without it.
  font-family: Helvetica, Arial, sans-serif;
  line-height: 1;
  white-space: pre;
}

.rp__rule {
  position: absolute;
  height: 1px;
}

.rp__img {
  position: absolute;
  display: block;
}

.rp__empty {
  margin: 0;
  color: $vf-ink-3;
  font-size: 12px;
}
</style>
